import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { Modal } from '../components/Modal';
import ChangePasswordForm from '../components/ChangePasswordForm';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import {
  loadStoredState,
  saveStoredState,
  Preferences
} from '../utils/mockState';

const PRIMARY_COLOR_PRESETS = [
  { name: 'Padrão Teal', hex: '#082129' },
  { name: 'Azul Noturno', hex: '#0c1b33' },
  { name: 'Grafite', hex: '#181b20' },
  { name: 'Roxo Deep', hex: '#1f1135' },
  { name: 'Verde Floresta', hex: '#09281a' },
  { name: 'Vinho Escuro', hex: '#221118' }
];

const SECONDARY_COLOR_PRESETS = [
  { name: 'Verde Água', hex: '#7cd3c6' },
  { name: 'Azul Celeste', hex: '#38bdf8' },
  { name: 'Laranja Express', hex: '#f97316' },
  { name: 'Amarelo Ouro', hex: '#facc15' },
  { name: 'Esmeralda', hex: '#10b981' },
  { name: 'Rosa Magenta', hex: '#ec4899' }
];

export default function Settings() {
  const { showToast } = useToast();
  const { me, reloadMe } = useAuth();
  const [state, setState] = useState(loadStoredState());
  const [activeTab, setActiveTab] = useState<'engine' | 'company' | 'users' | 'integrations' | 'billing' | 'security'>('engine');
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const [prefs, setPrefs] = useState<Preferences>(state.preferences);
  const [companyName, setCompanyName] = useState(me?.company?.name || state.user.company);
  const [userName, setUserName] = useState(me?.name || state.user.name);
  const [userEmail, setUserEmail] = useState(me?.email || state.user.email);
  const [cnpj, setCnpj] = useState(me?.company?.cnpj || '12.345.678/0001-90');
  const [phone, setPhone] = useState(me?.company?.phone || '(11) 3456-7890');

  // Cores e logo da empresa
  const [primaryColor, setPrimaryColor] = useState(me?.company?.primaryColor || '#082129');
  const [secondaryColor, setSecondaryColor] = useState(me?.company?.secondaryColor || '#7cd3c6');
  const [logoUrl, setLogoUrl] = useState(me?.company?.logoUrl || '');
  const [savingCompany, setSavingCompany] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setState(loadStoredState());
  }, []);

  useEffect(() => {
    if (me) {
      if (me.company?.name) setCompanyName(me.company.name);
      if (me.company?.cnpj) setCnpj(me.company.cnpj);
      if (me.company?.phone) setPhone(me.company.phone);
      if (me.company?.primaryColor) setPrimaryColor(me.company.primaryColor);
      if (me.company?.secondaryColor) setSecondaryColor(me.company.secondaryColor);
      if (me.company?.logoUrl !== undefined) setLogoUrl(me.company.logoUrl || '');
      if (me.name) setUserName(me.name);
      if (me.email) setUserEmail(me.email);
    }
  }, [me]);

  const handleSavePreferences = () => {
    const nextState = {
      ...state,
      preferences: prefs,
      user: {
        ...state.user,
        name: userName,
        company: companyName,
        email: userEmail
      }
    };
    setState(nextState);
    saveStoredState(nextState);
    showToast('Preferências de otimização salvas com sucesso!', 'success');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Por favor, selecione um arquivo de imagem válido (PNG, JPG, SVG, etc).', 'error');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showToast('A imagem deve ter no máximo 2MB.', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setLogoUrl(base64);
      showToast('Logo carregada! Clique em "Salvar perfil da empresa" para aplicar.', 'info');
    };
    reader.readAsDataURL(file);
  };

  const handleResetBranding = () => {
    setPrimaryColor('#082129');
    setSecondaryColor('#7cd3c6');
    setLogoUrl('');
    showToast('Cores e logotipo restaurados para os padrões do sistema.', 'info');
  };

  const handleSaveCompany = async () => {
    setSavingCompany(true);
    try {
      await api.put('/auth/company', {
        companyName,
        cnpj,
        phone,
        primaryColor,
        secondaryColor,
        logoUrl: logoUrl || null,
        userName,
        userEmail
      });

      await reloadMe();
      showToast('Perfil e identidade visual da empresa atualizados com sucesso!', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erro ao salvar dados da empresa.', 'error');
    } finally {
      setSavingCompany(false);
    }
  };

  return (
    <Layout>
      <div className="page-head">
        <div>
          <h1 className="page-title">Configurações do Sistema</h1>
          <p className="page-lede">
            Ajuste os parâmetros do algoritmo de roteirização, perfil da transportadora e preferências operacionais.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={activeTab === 'company' ? handleSaveCompany : handleSavePreferences} disabled={savingCompany}>
            {savingCompany ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </div>
      </div>

      <div className="settings-layout">
        {/* Left Sticky Subnav */}
        <div className="settings-nav">
          <button
            type="button"
            className={activeTab === 'engine' ? 'active' : ''}
            onClick={() => setActiveTab('engine')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
            Motor de Otimização
          </button>

          <button
            type="button"
            className={activeTab === 'company' ? 'active' : ''}
            onClick={() => setActiveTab('company')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 21h18M6 21V9l6-5 6 5v12" />
            </svg>
            Perfil da Empresa
          </button>

          <button
            type="button"
            className={activeTab === 'integrations' ? 'active' : ''}
            onClick={() => setActiveTab('integrations')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
            </svg>
            WhatsApp & APIs
          </button>

          <button
            type="button"
            className={activeTab === 'billing' ? 'active' : ''}
            onClick={() => setActiveTab('billing')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
              <line x1="1" y1="10" x2="23" y2="10"></line>
            </svg>
            Plano & Faturamento
          </button>

          <button
            type="button"
            className={activeTab === 'security' ? 'active' : ''}
            onClick={() => setActiveTab('security')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            Segurança
          </button>
        </div>

        {/* Right Content */}
        <div>
          {activeTab === 'engine' && (
            <div className="card" style={{ padding: 24 }}>
              <div className="card-head" style={{ padding: '0 0 14px', borderBottom: '1px solid var(--line-2)' }}>
                <div>
                  <div className="card-title">Parâmetros do Motor Heurístico</div>
                  <div className="card-sub">Configure como o algoritmo calcula as rotas e prioriza paradas</div>
                </div>
              </div>

              <div className="pref-item">
                <div>
                  <div className="k">Evitar praças de pedágio desnecessárias</div>
                  <div className="d">O algoritmo desviará de rodovias pedagiadas quando a rota alternativa for financeiramente mais vantajosa.</div>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={prefs.avoidTolls}
                    onChange={(e) => setPrefs({ ...prefs, avoidTolls: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="pref-item">
                <div>
                  <div className="k">Priorizar vias expressas e corredores</div>
                  <div className="d">Favorece marginais e avenidas principais mesmo que a distância em linha reta seja ligeiramente maior.</div>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={prefs.prioritizeSpeed}
                    onChange={(e) => setPrefs({ ...prefs, prioritizeSpeed: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="pref-item">
                <div>
                  <div className="k">Respeitar janelas de horário estritas</div>
                  <div className="d">Prioriza o cumprimento pontual do agendamento do cliente antes do trajeto mais curto.</div>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={prefs.respectTimeWindows}
                    onChange={(e) => setPrefs({ ...prefs, respectTimeWindows: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="pref-item">
                <div>
                  <div className="k">Retornar à base de origem (circuito fechado)</div>
                  <div className="d">Adiciona o trecho final de volta ao Centro de Distribuição ao calcular tempo e combustível.</div>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={prefs.returnToOrigin}
                    onChange={(e) => setPrefs({ ...prefs, returnToOrigin: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="pref-item">
                <div>
                  <div className="k">Limite máximo de paradas por rota</div>
                  <div className="d">Número máximo de pontos permitidos antes de exigir a divisão em múltiplos veículos.</div>
                </div>
                <div className="pref-slider-wrap">
                  <input
                    type="range"
                    min="10"
                    max="100"
                    step="5"
                    value={prefs.maxStopsPerRoute}
                    onChange={(e) => setPrefs({ ...prefs, maxStopsPerRoute: parseInt(e.target.value, 10) || 50 })}
                  />
                  <span className="mono" style={{ minWidth: 40, fontWeight: 700 }}>
                    {prefs.maxStopsPerRoute}
                  </span>
                </div>
              </div>

              <div className="pref-item">
                <div>
                  <div className="k">Velocidade média urbana considerada</div>
                  <div className="d">Usada para estimar a duração da rota em centros urbanos como São Paulo.</div>
                </div>
                <div className="pref-slider-wrap">
                  <input
                    type="range"
                    min="15"
                    max="60"
                    step="1"
                    value={prefs.avgSpeedUrban}
                    onChange={(e) => setPrefs({ ...prefs, avgSpeedUrban: parseInt(e.target.value, 10) || 32 })}
                  />
                  <span className="mono" style={{ minWidth: 60, fontWeight: 700 }}>
                    {prefs.avgSpeedUrban} km/h
                  </span>
                </div>
              </div>

              <div className="pref-item">
                <div>
                  <div className="k">Heurística de tráfego histórico</div>
                  <div className="d">Modelo estatístico utilizado para prever congestionamento por faixa horária.</div>
                </div>
                <select
                  className="input"
                  value={prefs.trafficHeuristic}
                  onChange={(e) => setPrefs({ ...prefs, trafficHeuristic: e.target.value as any })}
                  style={{ width: 220, padding: '7px 10px', fontSize: 13 }}
                >
                  <option value="realtime">Tempo real (Google Maps API)</option>
                  <option value="balanced">Balanceado (Histórico + ao vivo)</option>
                  <option value="historic">Apenas distâncias euclidianas</option>
                </select>
              </div>

              <div style={{ marginTop: 24, textAlign: 'right' }}>
                <button className="btn btn-primary" onClick={handleSavePreferences}>
                  Salvar preferências do motor
                </button>
              </div>
            </div>
          )}

          {activeTab === 'company' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Card 1: Identidade Visual & Cores */}
              <div className="card" style={{ padding: 24 }}>
                <div className="card-head" style={{ padding: '0 0 14px', borderBottom: '1px solid var(--line-2)' }}>
                  <div>
                    <div className="card-title">Identidade Visual & Cores do Sistema</div>
                    <div className="card-sub">Personalize a paleta de cores e o logotipo da sua transportadora na interface</div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, marginTop: 18 }}>
                  {/* Seletor Cor Principal */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 13.5, marginBottom: 4 }}>
                      Cor Principal (Fundo do Menu Lateral)
                    </label>
                    <div style={{ color: 'var(--text-3)', fontSize: 12.5, marginBottom: 10 }}>
                      Define a cor de fundo da barra de navegação lateral.
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <input
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        style={{
                          width: 44,
                          height: 38,
                          padding: 2,
                          borderRadius: 8,
                          border: '1px solid var(--line-2)',
                          cursor: 'pointer',
                          background: '#fff'
                        }}
                      />
                      <input
                        type="text"
                        className="input mono"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        style={{ width: 120, textTransform: 'uppercase' }}
                      />
                    </div>

                    {/* Presets */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {PRIMARY_COLOR_PRESETS.map((p) => (
                        <button
                          key={p.hex}
                          type="button"
                          onClick={() => setPrimaryColor(p.hex)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 8px',
                            borderRadius: 6,
                            border: primaryColor.toLowerCase() === p.hex.toLowerCase() ? '2px solid var(--teal-800)' : '1px solid var(--line-2)',
                            background: '#fff',
                            fontSize: 11.5,
                            cursor: 'pointer'
                          }}
                        >
                          <span style={{ width: 12, height: 12, borderRadius: 3, background: p.hex }} />
                          {p.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Seletor Cor Secundária */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 13.5, marginBottom: 4 }}>
                      Cor Secundária (Fontes & Destaques Ativos)
                    </label>
                    <div style={{ color: 'var(--text-3)', fontSize: 12.5, marginBottom: 10 }}>
                      Define a cor de realce, ícones ativos e badges do menu.
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <input
                        type="color"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        style={{
                          width: 44,
                          height: 38,
                          padding: 2,
                          borderRadius: 8,
                          border: '1px solid var(--line-2)',
                          cursor: 'pointer',
                          background: '#fff'
                        }}
                      />
                      <input
                        type="text"
                        className="input mono"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        style={{ width: 120, textTransform: 'uppercase' }}
                      />
                    </div>

                    {/* Presets */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {SECONDARY_COLOR_PRESETS.map((p) => (
                        <button
                          key={p.hex}
                          type="button"
                          onClick={() => setSecondaryColor(p.hex)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 8px',
                            borderRadius: 6,
                            border: secondaryColor.toLowerCase() === p.hex.toLowerCase() ? '2px solid var(--teal-800)' : '1px solid var(--line-2)',
                            background: '#fff',
                            fontSize: 11.5,
                            cursor: 'pointer'
                          }}
                        >
                          <span style={{ width: 12, height: 12, borderRadius: 3, background: p.hex }} />
                          {p.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Logotipo da Empresa */}
                <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid var(--line-2)' }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 13.5, marginBottom: 4 }}>
                    Logotipo / Ícone da Empresa
                  </label>
                  <div style={{ color: 'var(--text-3)', fontSize: 12.5, marginBottom: 14 }}>
                    Substitui o ícone padrão do sistema no topo do menu lateral e na barra superior.
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 12,
                        background: logoUrl ? 'rgba(255, 255, 255, 0.95)' : `linear-gradient(135deg, ${secondaryColor}, #0f4c5c)`,
                        border: '1px solid var(--line-2)',
                        display: 'grid',
                        placeItems: 'center',
                        overflow: 'hidden',
                        boxShadow: 'var(--shadow-1)'
                      }}
                    >
                      {logoUrl ? (
                        <img src={logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="10" r="3" />
                          <path d="M12 2a8 8 0 0 0-8 8c0 5.4 8 12 8 12s8-6.6 8-12a8 8 0 0 0-8-8z" />
                        </svg>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        📁 Fazer Upload de Imagem
                      </button>

                      {logoUrl && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setLogoUrl('')}
                          style={{ color: 'var(--danger)' }}
                        >
                          ✕ Usar Ícone Padrão
                        </button>
                      )}

                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={handleResetBranding}
                      >
                        ↺ Restaurar Padrões do Sistema
                      </button>
                    </div>
                  </div>
                </div>

                {/* Live Preview Mockup da Barra Lateral */}
                <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid var(--line-2)' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-2)', marginBottom: 10 }}>
                    Pré-visualização da Identidade Visual:
                  </div>

                  <div
                    style={{
                      background: primaryColor,
                      padding: '16px 20px',
                      borderRadius: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      maxWidth: 580,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 9,
                          background: logoUrl ? 'rgba(255, 255, 255, 0.1)' : `linear-gradient(135deg, ${secondaryColor}, #0f4c5c)`,
                          display: 'grid',
                          placeItems: 'center',
                          overflow: 'hidden'
                        }}
                      >
                        {logoUrl ? (
                          <img src={logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                            <circle cx="12" cy="10" r="3" />
                            <path d="M12 2a8 8 0 0 0-8 8c0 5.4 8 12 8 12s8-6.6 8-12a8 8 0 0 0-8-8z" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
                          {companyName || 'Sua Transportadora'}
                        </div>
                        <div style={{ color: secondaryColor, fontSize: 11.5, fontWeight: 600 }}>
                          ● Menu Personalizado
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <div
                        style={{
                          background: 'rgba(255, 255, 255, 0.08)',
                          color: '#fff',
                          padding: '6px 12px',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6
                        }}
                      >
                        <span style={{ color: secondaryColor }}>★</span> Item Ativo
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: Dados Cadastrais */}
              <div className="card" style={{ padding: 24 }}>
                <div className="card-head" style={{ padding: '0 0 14px', borderBottom: '1px solid var(--line-2)' }}>
                  <div>
                    <div className="card-title">Dados Cadastrais da Transportadora</div>
                    <div className="card-sub">Informações exibidas nos comprovantes e cabeçalhos</div>
                  </div>
                </div>

                <div className="field-row" style={{ marginTop: 16 }}>
                  <div className="field">
                    <label>Razão Social / Nome da Empresa</label>
                    <input
                      type="text"
                      className="input"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>CNPJ</label>
                    <input
                      type="text"
                      className="input mono"
                      value={cnpj}
                      onChange={(e) => setCnpj(e.target.value)}
                    />
                  </div>
                </div>

                <div className="field-row">
                  <div className="field">
                    <label>Nome do Responsável Operacional</label>
                    <input
                      type="text"
                      className="input"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>Email de Contato</label>
                    <input
                      type="email"
                      className="input"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                    />
                  </div>
                </div>

                <div className="field">
                  <label>Telefone / WhatsApp de Suporte</label>
                  <input
                    type="text"
                    className="input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>

                <div style={{ marginTop: 24, textAlign: 'right' }}>
                  <button className="btn btn-primary" onClick={handleSaveCompany} disabled={savingCompany}>
                    {savingCompany ? 'Salvando...' : 'Salvar perfil da empresa'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'integrations' && (
            <div className="card" style={{ padding: 24 }}>
              <div className="card-head" style={{ padding: '0 0 14px', borderBottom: '1px solid var(--line-2)' }}>
                <div>
                  <div className="card-title">Integrações & Conexões Externas</div>
                  <div className="card-sub">Chaves de API e webhooks para automação com seu ERP / TMS</div>
                </div>
              </div>

              <div className="field" style={{ marginTop: 16 }}>
                <label>Token de Acesso da API (REST)</label>
                <div className="input-wrap">
                  <input
                    type="text"
                    className="input mono"
                    readOnly
                    value="otr_live_9921837491823749812739487"
                  />
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ position: 'absolute', right: 8, top: 6 }}
                    onClick={() => showToast('Token copiado para a área de transferência!', 'info')}
                  >
                    Copiar
                  </button>
                </div>
              </div>

              <div className="field" style={{ marginTop: 14 }}>
                <label>Webhook de Status de Entrega (URL POST)</label>
                <input
                  type="url"
                  className="input mono"
                  placeholder="https://sua-empresa.com.br/api/webhooks/entregas"
                />
              </div>

              <div style={{ marginTop: 24, textAlign: 'right' }}>
                <button className="btn btn-primary" onClick={() => showToast('Configurações de integração salvas!', 'success')}>
                  Salvar integrações
                </button>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="card" style={{ padding: 24 }}>
              <div className="card-head" style={{ padding: '0 0 14px', borderBottom: '1px solid var(--line-2)' }}>
                <div>
                  <div className="card-title">Segurança da Conta</div>
                  <div className="card-sub">Gerencie o acesso pessoal ao sistema</div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginTop: 18, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 260, flex: '1 1 320px' }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>Senha de acesso</div>
                  <div style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 4 }}>
                    Recomendamos trocar a senha periodicamente e sempre que suspeitar
                    que alguém tenha tido acesso indevido à sua conta.
                  </div>
                  {me?.mustChangePassword && (
                    <div
                      role="status"
                      style={{
                        marginTop: 10,
                        background: 'rgba(231, 111, 81, 0.12)',
                        border: '1px solid rgba(231, 111, 81, 0.35)',
                        color: '#7c2d12',
                        padding: '8px 10px',
                        borderRadius: 6,
                        fontSize: 12.5
                      }}
                    >
                      Sua senha atual é provisória. Defina uma senha pessoal.
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setPasswordModalOpen(true)}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: 'middle' }}>
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  Alterar minha senha
                </button>
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="card" style={{ padding: 24 }}>
              <div className="card-head" style={{ padding: '0 0 14px', borderBottom: '1px solid var(--line-2)' }}>
                <div>
                  <div className="card-title">Plano & Faturamento</div>
                  <div className="card-sub">Gerencie sua assinatura, limites de veículos e faturas</div>
                </div>
              </div>

              <div
                style={{
                  background: 'var(--paper-2)',
                  border: '1px solid var(--line-2)',
                  borderRadius: 12,
                  padding: 20,
                  marginTop: 16,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--teal-800)' }}>Plano Enterprise Pro</div>
                  <div style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 4 }}>
                    Até 50 veículos · Solver OR-Tools VRP · BI Financeiro · WhatsApp Ilimitado
                  </div>
                </div>
                <span className="badge b-teal" style={{ fontSize: 13, padding: '4px 10px' }}>Ativo</span>
              </div>

              <div style={{ marginTop: 24, textAlign: 'right' }}>
                <button className="btn btn-secondary" onClick={() => showToast('Redirecionando para o portal de faturamento...', 'info')}>
                  Gerenciar Faturas
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        title="Alterar minha senha"
      >
        <ChangePasswordForm
          onDone={() => setPasswordModalOpen(false)}
          onCancel={() => setPasswordModalOpen(false)}
        />
      </Modal>
    </Layout>
  );
}
